import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '@Hooks/index';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import {
  useGetPurchaseDocumentQuery,
  useGetPurchaseRequestQuery,
} from '@Features/MaterialRequest/requests/PurchaseRequest/store/api/purchaseRequestApiSlice';
import {
  DocumentListResponse,
  PurchaseDocumentResponse,
} from '@Types/api/form';
import { getDocument, getFileTypeExt } from '@Features/utils/document';
import { MInput, MSkeleton } from '@Components/Base';
import { findPurchaseDocumentItemBySearchValue } from '@Features/MaterialRequest/requests/utils/finder';
import { NoData } from '@Components/RequestList/noData';
import { getDocumentFolder } from '@Features/MaterialRequest/requests/MaterialRequisition/form/utils/document';
import { useFormListMutation } from '@Store/api/dmsApiSlice';
import MaterialDocumentItem from './MaterialDocumentItem';

import styles from '../view-tab.module.css';

const DocumentsTab: React.FC = () => {
  const requestId = useAppSelector(getModalRecordKey);
  const { data } = useGetPurchaseRequestQuery(requestId!, {
    skip: !requestId,
    refetchOnMountOrArgChange: true,
  });
  const { data: purchaseDocuments, isSuccess } = useGetPurchaseDocumentQuery(
    requestId,
    {
      skip: !requestId,
      refetchOnMountOrArgChange: true,
    }
  );
  const [getFormList] = useFormListMutation();
  const [searchedDocItems, setSearchedDocItems] = useState<
    Array<PurchaseDocumentResponse>
  >([]);
  const [documentsInFolder, setDocumentsInFolder] =
    useState<DocumentListResponse[]>();

  useEffect(() => {
    if (purchaseDocuments?.length && data?.mrId) {
      const documentFolderPath = getDocumentFolder(data?.mrId);
      setSearchedDocItems(purchaseDocuments);

      getFormList(documentFolderPath)
        .unwrap()
        .then((response) => {
          setDocumentsInFolder(response);
        });
    }
  }, [purchaseDocuments, data?.mrId]);

  const handleSearchDocumentItems = useCallback(
    (value: string) => {
      const result = findPurchaseDocumentItemBySearchValue(
        purchaseDocuments!,
        value
      );
      setSearchedDocItems(result);
    },
    [purchaseDocuments]
  );

  const handleDownload = (url: string | undefined, filePath: string): void => {
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  return (
    <div className={styles.viewTabMaterials}>
      <MInput
        className={styles.search}
        searchInput
        placeholder="Search..."
        onChange={(event) => handleSearchDocumentItems(event.target.value)}
      />
      {isSuccess ? (
        <div className={styles.container}>
          {searchedDocItems?.length ? (
            <>
              {searchedDocItems.map((document) => {
                // TODO: need to check docExtId instead of getDocument
                const matchedDocument =
                  documentsInFolder &&
                  getDocument(documentsInFolder, document.filePath);

                return (
                  <MaterialDocumentItem
                    description={document.description}
                    name={document.documentName}
                    status={Boolean(document.isDeleted)}
                    type={getFileTypeExt(document.filePath)}
                    key={`${document.documentName}-${document.filePath}-${document.documentId}`}
                    isConfidential={Boolean(document.isConfidential)}
                    filePath={document.filePath}
                    documentId={matchedDocument?.documentId}
                    onDownload={handleDownload}
                  />
                );
              })}
            </>
          ) : (
            <NoData context="documents" />
          )}
        </div>
      ) : (
        <div className={styles.container}>
          <MSkeleton repeat={2} className="skeleton-item" />
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
